import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { useMarkDeceased } from '../network/schemes.api';
import { useToast } from '../../../components/ui/Toast';

interface MarkDeceasedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  schemeId: string;
  subscriberId: string;
  customerName?: string;
}

export function MarkDeceasedDialog({
  isOpen,
  onClose,
  schemeId,
  subscriberId,
  customerName,
}: MarkDeceasedDialogProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const deceasedMutation = useMarkDeceased(schemeId);

  const handleMark = async () => {
    try {
      await deceasedMutation.mutateAsync({ subscriberId, notes: notes || undefined });
      toast({
        type: 'success',
        title: 'Deceased Marked',
        description: customerName
          ? `${customerName} has been marked as deceased.`
          : 'Subscriber has been marked as deceased.',
      });
      setNotes('');
      setConfirmed(false);
      onClose();
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({
        type: 'error',
        title: 'Failed to Mark',
        description: err instanceof Error ? err.message : 'Failed to mark subscriber as deceased.',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); setNotes(''); setConfirmed(false); } }}>
      <DialogContent className="sm:max-w-md bg-surface-1">
        <DialogHeader>
          <DialogTitle>Mark Subscriber as Deceased</DialogTitle>
          <DialogDescription>
            This action records the subscriber's passing and transitions their subscription to closure.
            {customerName && <span className="block mt-1 font-medium text-text-primary">{customerName}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-warning-bg border border-warning-500 rounded-[6px] p-3 text-xs text-warning-text">
            <p className="font-semibold">Important</p>
            <p className="mt-1">This action is irreversible. All pending dues and prize payouts will be settled through the deceased settlement process.</p>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-text-secondary uppercase tracking-wide">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Date of passing, relationship to nominee..."
              className="w-full text-sm p-2.5 bg-surface-2 border border-border rounded-[6px] focus:outline-none focus:border-accent-500 min-h-[80px] resize-y text-text-primary"
            />
          </div>

          <label className="flex items-start gap-2 p-3 bg-surface-2 border border-border rounded-[6px] cursor-pointer">
            <Input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 accent-accent-500"
            />
            <span className="text-xs text-text-secondary leading-relaxed">
              I confirm that the subscriber is deceased and that I am authorized to mark this record. This action cannot be undone.
            </span>
          </label>
        </div>

        <DialogFooter className="gap-3">
          <Button variant="secondary" onClick={() => { onClose(); setNotes(''); setConfirmed(false); }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleMark}
            loading={deceasedMutation.isPending}
            disabled={!confirmed}
          >
            Confirm Deceased
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
